<template>
    <div v-if="details" class="news-details px-0 pt-3 pb-0">
        <v-card class="pa-2" flat tile>
            <v-row>
                <!-- back btn & categories & brands -->
                <v-col cols="12" class="py-0 mb-2">
                    <v-row no-gutters class="d-flex flex-nowrap align-center">
                        <v-col cols="auto" class="d-flex align-center flex-grow-1 flex-shrink-1 ma-0 pa-0 body-2 secondary--text text-truncate">
                            <back-button v-if="!isFetchingNewsDetail"/>
                            <v-skeleton-loader type="avatar" v-else/>

                            <!-- categories -->
                            <v-tooltip
                                    attach
                                    bottom
                                    class="tooltip--custom"
                                    color="secondary"
                                    :disabled="showToolbar"
                                    nudge-bottom="5px"
                                    nudge-left="100%"
                                    :open-on-click="!hoverToolbar"
                                    :open-on-hover="hoverToolbar"
                                    v-if="!isFetchingNewsDetail">
                                <template v-slot:activator="{ on }">
                                    <span v-for="(category, index) in sortedCategories.slice().reverse()"
                                          :key="category.id"
                                          v-on="on"
                                          small
                                          :class="{ 'ml-1': index + 1 != sortedCategories.length }">
                                        {{ category.name }}
                                    </span>
                                </template>
                                <div class="tooltip--bubbly-top">
                                    <div v-for="category in sortedCategories"
                                         :key="category.id"
                                         small
                                         class="my-1 body-2">
                                        {{ category.name }}
                                    </div>
                                </div>
                            </v-tooltip>
                            <v-skeleton-loader type="chip" v-else class="ml-4"/>
                            <!-- /categories -->
                        </v-col>

                        <!-- related brands -->
                        <v-col cols="auto" class="d-flex align-center flex-shrink-0">
                            <template v-if="!isFetchingNewsDetail">
                                <v-img v-for="(brand, index) in details.related_brands"
                                       :key="brand.id"
                                       :class="{ 'mr-1': index + 1 != details.related_brands.length }"
                                       max-width="50px"
                                       width="100%"
                                       max-height="18px"
                                       contains
                                       :src="brand.image"></v-img>
                            </template>

                            <!-- skeleton loading -->
                            <template v-else>
                                <v-skeleton-loader type="chip"/>
                            </template>
                            <!-- /skeleton loading -->
                        </v-col>
                        <!-- /related brands -->
                    </v-row>
                </v-col>
                <!-- /back btn & categories & brands -->

                <!-- news image -->
                <v-col v-if="details.image_header && details.image_header.source" cols="12" sm="5" class="py-0">
                    <v-img aspect-ratio="1" :src="details.image_header.source" v-if="!isFetchingNewsDetail"></v-img>
                    <v-skeleton-loader type="image@2" tile flat v-else/>
                </v-col>
                <!-- /news image -->

                <!-- news content -->
                <v-col cols="12" :sm="details.image_header && details.image_header.source ? 7 : 12" class="py-0">
                    <!-- news info -->
                    <div class="subtitle-1 secondary--text text-uppercase px-5 mt-6 mt-sm-0 pt-0">
                        <template v-if="!isFetchingNewsDetail">
                            {{ details.date_of_news | date }}
                        </template>
                        <v-skeleton-loader type="chip" class="mb-1" flat v-else/>
                    </div>
                    <h1 class="headline px-5 font-weight-bold">
                        <template v-if="!isFetchingNewsDetail">
                            {{ details.headline }}
                        </template>
                        <v-skeleton-loader type="heading" class="mb-1" v-else/>
                    </h1>
                    <!-- /news info -->

                    <!-- news details -->
                    <div class="px-5 mt-6 pt-0">
                        <anchor-content :content="details.content" v-if="!isFetchingNewsDetail"/>
                        <template v-else>
                            <v-skeleton-loader type="paragraph@3" class="mb-2"/>
                            <v-skeleton-loader type="sentences@3"/>
                            <v-skeleton-loader type="paragraph@3" class="mb-2"/>
                            <v-skeleton-loader type="sentences@3"/>
                        </template>
                    </div>
                    <!-- /news details -->
                </v-col>
                <!-- /news content -->
            </v-row>
        </v-card>

        <!-- news image gallery & downloads -->
        <v-row class="mt-5"
               v-if="details.image_gallery.length > 0 || details.downloads.length > 0">

            <!-- downloads -->
            <v-col cols="12" sm="5" class="py-0" v-if="details.downloads.length > 0">
                <heading-section :loading="isFetchingNewsDetail" id="downloads" class="d-flex">
                    <div v-if="details.custom_downloads_headline">{{ details.custom_downloads_headline }}</div>
                    <div v-else>{{ $vuetify.lang.t('$vuetify.title.downloads') }}</div>
                </heading-section>
                <v-card class="mt-2 pa-2" flat tile>
                    <v-list two-line v-if="!isFetchingNewsDetail">
                        <v-list-item @click="downloadHandler(download.source, download.id)"
                                     v-for="download in details.downloads"
                                     :key="download.id"
                                     :disabled="$wait.is(`download-${download.id}`)"
                                     :loading="$wait.is(`download-${download.id}`)">
                            <v-list-item-icon>
                                <v-icon>mdi-file-download-outline</v-icon>
                            </v-list-item-icon>

                            <v-list-item-content>
                                <v-list-item-title class="subtitle-1 font-weight-bold">{{ download.name }}</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ download.description }}</v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-icon>
                                <v-icon>mdi-download</v-icon>
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>

                    <v-skeleton-loader type="image" tile flat v-else/>
                </v-card>
            </v-col>
            <!-- /downloads -->

            <!-- image gallery -->
            <v-col cols="12" sm="7"
                   class="py-0 ml-auto"
                   :class="{ 'mt-5 mt-sm-0' : details.downloads.length > 0}"
                   v-if="details.image_gallery.length > 0">
                <heading-section :loading="isFetchingNewsDetail" id="gallery" class="d-flex">
                    <div v-if="details.custom_gallery_headline">{{ details.custom_gallery_headline }}</div>
                    <div v-else>{{ $vuetify.lang.t('$vuetify.title.news_slider') }}</div>
                </heading-section>

                <v-card class="pa-2" flat tile>
                    <div class="position-relative" v-if="!isFetchingNewsDetail">
                        <v-carousel pa-0
                                    height="auto"
                                    hide-delimiters
                                    :show-arrows="false"
                                    v-model="carouselIndex">
                            <v-carousel-item
                                    v-for="image in details.image_gallery"
                                    :key="image.id">
                                <v-img :src="image.source" aspect-ratio="1"></v-img>
                            </v-carousel-item>
                            <div class="v-carousel__controls"
                                 v-if="details.image_gallery.length > 1">
                                <v-icon class="mr-3" @click="carouselIndex--" :disabled="!(carouselIndex > 0)">
                                    mdi-chevron-left
                                </v-icon>
                                {{ `${carouselIndex+1}/${details.image_gallery.length}` }}
                                <v-icon class="ml-3" @click="carouselIndex++" :disabled="!(carouselIndex < details.image_gallery.length - 1)">
                                    mdi-chevron-right
                                </v-icon>
                            </div>
                        </v-carousel>
                        <v-expand-transition>
                            <div v-if="details.image_gallery[carouselIndex].description" class="v-carousel__caption">
                                <v-scroll-y-transition mode="out-in">
                                    <p class="px-5 pt-4 pb-2 mb-0 body-1"
                                       :key="details.image_gallery[carouselIndex].id">
                                        {{ details.image_gallery[carouselIndex].description }}
                                    </p>
                                </v-scroll-y-transition>
                            </div>
                        </v-expand-transition>
                    </div>

                    <v-skeleton-loader type="image@2" tile flat v-else/>
                </v-card>
            </v-col>
            <!-- /image gallery -->
        </v-row>
        <!-- /news image gallery & downloads -->
    </div>

    <!-- page error -->
    <v-container v-else>
        <page-not-found/>
    </v-container>
    <!-- /page error -->
</template>

<script>
    // components
    import AnchorContent from '../../components/AnchorContent';
    import BackButton from '../../components/BackButton';
    import HeadingSection from '../../components/HeadingSection';
    import PageNotFound from '../../components/PageNotFound';
    // models
    import News from '../../models/News';
    // utils
    import { setPageTitle, download, checkDownloadPermission } from '../../utils/helper';
    import { LOADER_FETCH_NEWS_DETAIL } from '../../utils/constants';

    export default {
        name: 'news-detail',
        components: {
            AnchorContent,
            BackButton,
            HeadingSection,
            PageNotFound,
        },
        props: ['newsId'],
        data() {
            return {
                carouselIndex: 0,
            }
        },
        methods: {
            async downloadHandler(url, id) {
                return checkDownloadPermission(url, id);
            },
        },
        computed: {
            details() {
                return News.getDetails(this.newsId);
            },
            notFoundTitle() {
                return this.$vuetify.lang.t('$vuetify.page_not_found.page_title');
            },
            sortedCategories() {
                return this.details.related_categories.sort((a, b) => Number(a.id) - Number(b.id));
            },
            isFetchingNewsDetail() {
                return this.$wait.is(LOADER_FETCH_NEWS_DETAIL);
            },
            hoverToolbar() {
                return this.$vuetify.breakpoint.mdAndUp;
            },
            showToolbar() {
                return this.$vuetify.breakpoint.lgAndUp;
            },
        },
        async mounted() {
            await News.fetchDetails(this.newsId);
        },
    }
</script>
